import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import QuickActionButton from '../../components/ui/QuickActionButton';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CameraViewfinder from './components/CameraViewfinder';

import VehicleDataCard from './components/VehicleDataCard';
import ScanHistory from './components/ScanHistory';
import OfflineIndicator from './components/OfflineIndicator';
import QuickActions from './components/QuickActions';
import CreateOrderModal from './components/CreateOrderModal';
import vinService from '../../services/vinService';
import { orderService } from '../../services/orderService';
import { vehicleService } from '../../services/vehicleService';
import scanHistoryService from '../../services/scanHistoryService';
import { useAuth } from '../../contexts/AuthContext';

const VinScanner = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isScanning, setIsScanning] = useState(true); // Start scanning by default
  const [vehicleData, setVehicleData] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [pendingSyncs, setPendingSyncs] = useState(0);
  const [activeTab, setActiveTab] = useState('camera');

  // Error state for API issues
  const [error, setError] = useState(null);

  useEffect(() => {
    // Load scan history from database
    const loadScanHistory = async () => {
      if (user?.id) {
        try {
          const { data, error } = await scanHistoryService.getScanHistory(user.id);
          if (error) {
            console.error('Error loading scan history:', error);
            setScanHistory([]);
          } else {
            // Transform database data to match the expected format
            const transformedHistory = data?.map(scan => ({
              id: scan.id,
              vin: scan.vin,
              make: scan.make,
              model: scan.model,
              year: scan.year,
              engine: scan.engine,
              transmission: scan.transmission,
              fuelType: scan.fuel_type,
              bodyType: scan.body_type,
              color: scan.color,
              doors: scan.doors,
              displacement: scan.displacement,
              power: scan.power,
              cylinders: scan.cylinders,
              driveType: scan.drive_type,
              trim: scan.trim,
              series: scan.series,
              country: scan.country,
              plant: scan.plant,
              capturedImage: scan.captured_image,
              timestamp: scan.created_at,
              hasOrder: scan.has_order,
              source: scan.source,
              isUnknown: scan.is_unknown,
              error: scan.error_message
            })) || [];
            setScanHistory(transformedHistory);
          }
        } catch (error) {
          console.error('Error loading scan history:', error);
          setScanHistory([]);
        }
      }
    };

    loadScanHistory();

    // Setup online/offline listeners
    const handleOnline = () => {
      setIsOffline(false);
      syncPendingData();
    };
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [user?.id]);

  const syncPendingData = () => {
    // This function is no longer needed since we're using the database
    // Keeping it for potential future offline functionality
    setPendingSyncs(0);
  };

  const handleVinDetected = async (vin, capturedImage = null) => {
    console.log('🎯 handleVinDetected called with VIN:', vin); // Debug log
    setIsLoading(true);
    setError(null);
    
    try {
      // Validate VIN format
      if (!vinService.validateVin(vin)) {
        console.log('❌ VIN validation failed in handleVinDetected'); // Debug log
        throw new Error('Ungültiges VIN-Format. VIN muss 17 Zeichen lang sein und darf keine I, O oder Q enthalten.');
      }
      console.log('✅ VIN validation passed in handleVinDetected'); // Debug log

      // Check cache first
      let vehicleInfo = vinService.getCachedVinData(vin);
      console.log('💾 Cached data found:', !!vehicleInfo); // Debug log
      
      // Temporarily disable cache to test Zyla API
      // TODO: Remove this after testing
      vehicleInfo = null;
      console.log('🧹 Cache disabled for testing'); // Debug log
      
      if (!vehicleInfo) {
        console.log('🌐 No cache, calling Zyla API...'); // Debug log
        // Call Zyla API
        vehicleInfo = await vinService.decodeVin(vin);
        console.log('🌐 API call completed, caching result...'); // Debug log
        
        // Cache the result
        vinService.cacheVinData(vin, vehicleInfo);
      } else {
        console.log('💾 Using cached data'); // Debug log
      }

      // Add captured image and timestamp
      const enrichedData = {
        ...vehicleInfo,
        capturedImage,
        timestamp: new Date()?.toISOString()
      };
      
      setVehicleData(enrichedData);
      
      // Save scan to database
      if (user?.id) {
        try {
          const { data: savedScan, error: saveError } = await scanHistoryService.saveScan(enrichedData, user.id);
          if (saveError) {
            console.error('Error saving scan to database:', saveError);
          } else {
            // Add to local state with database ID
            const scanWithId = { ...enrichedData, id: savedScan.id };
            const newHistory = [scanWithId, ...scanHistory?.slice(0, 9)];
            setScanHistory(newHistory);
          }
        } catch (error) {
          console.error('Error saving scan:', error);
        }
      }
      
      setActiveTab('result');
    } catch (error) {
      console.error('VIN processing error:', error);
      setError(error.message);
      
      // Handle unknown VIN
      const unknownVehicle = {
        vin,
        make: 'Unbekannt',
        model: 'Fahrzeugdaten nicht gefunden',
        year: 'N/A',
        capturedImage,
        timestamp: new Date()?.toISOString(),
        isUnknown: true,
        error: error.message
      };
      setVehicleData(unknownVehicle);
      setActiveTab('result');
    } finally {
      setIsLoading(false);
      setIsScanning(false);
    }
  };

  const handleCreateOrder = () => {
    if (!vehicleData) return;
    setIsOrderModalOpen(true);
  };

  const handleOrderSubmit = async (orderData) => {
    setIsCreatingOrder(true);
    
    try {
      if (!user?.id) {
        throw new Error('Benutzer nicht angemeldet');
      }

      // First, check if vehicle exists in database, if not create it
      let vehicleId = null;
      try {
        const { data: existingVehicle } = await vehicleService.getVehicleByVIN(vehicleData.vin);
        if (existingVehicle) {
          vehicleId = existingVehicle.id;
        }
      } catch (vehicleError) {
      }

      // If vehicle doesn't exist, create it
      if (!vehicleId) {
        // Helper function to validate and normalize enum values
        const normalizeFuelType = (fuelType) => {
          if (!fuelType || fuelType.toLowerCase() === 'n/a' || fuelType.toLowerCase() === 'unknown') {
            return 'gasoline'; // Default fallback
          }
          const normalized = fuelType.toLowerCase();
          // Map common variations to valid enum values
          const fuelTypeMap = {
            'petrol': 'gasoline',
            'diesel': 'diesel',
            'electric': 'electric',
            'hybrid': 'hybrid',
            'gasoline': 'gasoline',
            'gas': 'gasoline'
          };
          return fuelTypeMap[normalized] || 'gasoline';
        };

        const normalizeTransmission = (transmission) => {
          if (!transmission || transmission.toLowerCase() === 'n/a' || transmission.toLowerCase() === 'unknown') {
            return 'automatic'; // Default fallback
          }
          const normalized = transmission.toLowerCase();
          // Map common variations to valid enum values
          const transmissionMap = {
            'automatic': 'automatic',
            'manual': 'manual',
            'cvt': 'cvt',
            'semi-automatic': 'semi_automatic'
          };
          return transmissionMap[normalized] || 'automatic';
        };

        const vehiclePayload = {
          vin: vehicleData.vin,
          make: vehicleData.make,
          model: orderData.vehicleData.model, // Use modified model from modal
          year: parseInt(orderData.vehicleData.year) || new Date().getFullYear(), // Use modified year from modal
          engine_size: vehicleData.engine,
          color: orderData.color || vehicleData.color,
          fuel_type: normalizeFuelType(vehicleData.fuelType),
          transmission: normalizeTransmission(vehicleData.transmission),
          car_house_id: orderData.selectedCarHouse,
          additional_info: {
            scanned_at: new Date().toISOString(),
            original_data: vehicleData
          }
        };

        const { data: newVehicle, error: vehicleCreateError } = await vehicleService.createVehicle(vehiclePayload);
        if (vehicleCreateError) {
          throw new Error(`Fehler beim Erstellen des Fahrzeugs: ${vehicleCreateError.message}`);
        }
        vehicleId = newVehicle.id;
      }

      // Prepare order data to match dashboard format
      // Let the database auto-generate the order number using the trigger
              const orderPayload = {
          vehicle_id: vehicleId,
          car_house_id: orderData.selectedCarHouse,
          assigned_technician_id: orderData.assignedTechnician,
          service_type_id: orderData.serviceType, // Add service_type_id
          title: `Antrag für ${vehicleData.vin}`,
          description: orderData.notes || `Serviceauftrag für ${vehicleData.make} ${vehicleData.model} (${vehicleData.vin})`,
          customer_name: 'Kunde', // Default customer name
          customer_phone: '', // Will be filled later
          customer_email: '', // Will be filled later
          status: 'pending',
          priority: 'normal',
          work_notes: orderData.notes || '',
          vin: vehicleData.vin, // Add VIN directly to order for easier access
          license_plate: orderData.licensePlate || '', // Add license plate from form
          color: orderData.color || '', // Add color from form
          mileage: 0, // Add mileage field
          return_date: orderData.returnDate || null, // Add return date from form
          return_time: orderData.returnTime || null, // Add return time from form
          total_amount: parseFloat(orderData.serviceTypePrice) || 0, // Set total amount based on service type price
          metadata: {
            created_via: 'vin_scanner',
            scanned_vin: vehicleData.vin,
            service_type: orderData.serviceType
          }
        };

      // Create the order
      const { data: createdOrder, error: orderError } = await orderService.createOrder(orderPayload, user.id);
      
      if (orderError) {
        throw new Error(`Fehler beim Erstellen des Auftrags: ${orderError.message}`);
      }

      
      // Update scan history to mark as having order
      if (user?.id) {
        try {
          await scanHistoryService.updateScanOrderStatus(vehicleData.vin, user.id, true);
        } catch (error) {
          console.error('Error updating scan order status:', error);
        }
      }
      
      const updatedHistory = scanHistory?.map(scan => 
        scan?.vin === vehicleData?.vin ? { ...scan, hasOrder: true } : scan
      );
      setScanHistory(updatedHistory);
      
      alert('Auftrag erfolgreich erstellt!');
      
      // Close modal and navigate to order management
      setIsOrderModalOpen(false);
      navigate('/order-management-dashboard', {
        state: { newOrder: createdOrder }
      });
    } catch (error) {
      console.error('Order creation error:', error);
      alert(`Fehler beim Erstellen des Auftrags: ${error.message}`);
    } finally {
      setIsCreatingOrder(false);
    }
  };

  const handleSaveVin = () => {
    if (!vehicleData) return;
    
    // VIN is already saved in history, just show confirmation
    alert('VIN wurde erfolgreich gespeichert!');
  };

  const handleNewScan = () => {
    setVehicleData(null);
    setActiveTab('camera');
    setIsScanning(true);
  };

  const refreshScanHistory = async () => {
    if (user?.id) {
      try {
        const { data, error } = await scanHistoryService.getScanHistory(user.id);
        if (error) {
          console.error('Error refreshing scan history:', error);
        } else {
          // Transform database data to match the expected format
          const transformedHistory = data?.map(scan => ({
            id: scan.id,
            vin: scan.vin,
            make: scan.make,
            model: scan.model,
            year: scan.year,
            engine: scan.engine,
            transmission: scan.transmission,
            fuelType: scan.fuel_type,
            bodyType: scan.body_type,
            color: scan.color,
            doors: scan.doors,
            displacement: scan.displacement,
            power: scan.power,
            cylinders: scan.cylinders,
            driveType: scan.drive_type,
            trim: scan.trim,
            series: scan.series,
            country: scan.country,
            plant: scan.plant,
            capturedImage: scan.captured_image,
            timestamp: scan.created_at,
            hasOrder: scan.has_order,
            source: scan.source,
            isUnknown: scan.is_unknown,
            error: scan.error_message
          })) || [];
          setScanHistory(transformedHistory);
        }
      } catch (error) {
        console.error('Error refreshing scan history:', error);
      }
    }
  };

  const handleSelectHistoryVin = (scan) => {
    setVehicleData(scan);
    setActiveTab('result');
  };

  // Refresh scan history when component becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && user?.id) {
        refreshScanHistory();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user?.id]);

  const handleClearHistory = async () => {
    if (confirm('Möchten Sie den gesamten Scan-Verlauf löschen?')) {
      try {
        if (user?.id) {
          const { error } = await scanHistoryService.clearScanHistory(user.id);
          if (error) {
            console.error('Error clearing scan history:', error);
            alert('Fehler beim Löschen des Scan-Verlaufs');
            return;
          }
        }
        setScanHistory([]);
        setPendingSyncs(0);
      } catch (error) {
        console.error('Error clearing scan history:', error);
        alert('Fehler beim Löschen des Scan-Verlaufs');
      }
    }
  };

  const tabs = [
    { id: 'camera', label: 'Kamera-Scan', icon: 'Camera' },
    { id: 'result', label: 'Ergebnis', icon: 'CheckCircle', disabled: !vehicleData },
    { id: 'history', label: 'Verlauf', icon: 'History' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header userRole="admin" />
             {/* Hide sidebar on mobile */}
       <div className="hidden lg:block">
         <Sidebar 
           isCollapsed={isSidebarCollapsed}
           onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
           userRole="admin"
         />
       </div>
      <OfflineIndicator isOffline={isOffline} pendingSyncs={pendingSyncs} />
      <main className={`pt-16 pb-20 lg:pb-8 transition-all duration-200 ${
        isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
      }`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Breadcrumb />
          
          {/* Page Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-foreground mb-2">VIN Scanner</h1>
                <p className="text-muted-foreground">
                  Scannen Sie Fahrzeugidentifikationsnummern und erstellen Sie Serviceaufträge
                </p>
              </div>
              
              <div className="hidden lg:flex items-center space-x-3">
                <Button
                  variant="outline"
                  onClick={() => navigate('/order-management-dashboard')}
                  iconName="ClipboardList"
                  iconPosition="left"
                >
                  Aufträge
                </Button>
                <Button
                  variant="default"
                  onClick={handleNewScan}
                  iconName="Scan"
                  iconPosition="left"
                >
                  Neuer Scan
                </Button>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="mb-6">
            <div className="border-b border-border">
              <nav className="flex space-x-8 overflow-x-auto">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => !tab?.disabled && setActiveTab(tab?.id)}
                    disabled={tab?.disabled}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                      activeTab === tab?.id
                        ? 'border-primary text-primary'
                        : tab?.disabled
                        ? 'border-transparent text-muted-foreground cursor-not-allowed opacity-50'
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                    }`}
                  >
                    <Icon name={tab?.icon} size={16} />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-4">
              <div className="flex items-center">
                <Icon name="AlertTriangle" size={20} className="text-error mr-3" />
                <div>
                  <h4 className="text-sm font-medium text-error">API-Fehler</h4>
                  <p className="text-sm text-error/80 mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Tab Content */}
          <div className="space-y-6">
            {activeTab === 'camera' && (
              <div className="space-y-6">
                <CameraViewfinder
                  onVinDetected={handleVinDetected}
                  isScanning={isScanning}
                  onToggleCamera={() => setIsScanning(!isScanning)}
                />
                
                <div className="lg:hidden">
                  <QuickActions
                    vehicleData={vehicleData}
                    onNewScan={handleNewScan}
                  />
                </div>
              </div>
            )}



            {activeTab === 'result' && vehicleData && (
              <div className="space-y-6">
                <VehicleDataCard
                  vehicleData={vehicleData}
                  onCreateOrder={handleCreateOrder}
                  onSaveVin={handleSaveVin}
                  isCreatingOrder={isCreatingOrder}
                />
                
                <div className="lg:hidden">
                  <QuickActions
                    vehicleData={vehicleData}
                    onNewScan={handleNewScan}
                  />
                </div>
              </div>
            )}

            {activeTab === 'history' && (
              <div className="space-y-6">
                <ScanHistory
                  scanHistory={scanHistory}
                  onSelectVin={handleSelectHistoryVin}
                  onClearHistory={handleClearHistory}
                />
                
                <div className="lg:hidden">
                  <QuickActions
                    vehicleData={vehicleData}
                    onNewScan={handleNewScan}
                  />
                </div>
              </div>
            )}

            {/* Desktop Quick Actions */}
            <div className="hidden lg:block">
              <QuickActions
                vehicleData={vehicleData}
                onNewScan={handleNewScan}
              />
            </div>
          </div>
        </div>
      </main>
      
      {/* Create Order Modal */}
      <CreateOrderModal
        isOpen={isOrderModalOpen}
        onClose={() => setIsOrderModalOpen(false)}
        vehicleData={vehicleData}
        onCreateOrder={handleOrderSubmit}
        isLoading={isCreatingOrder}
      />
      
      <QuickActionButton userRole="admin" />
    </div>
  );
};

export default VinScanner;